import { create } from 'zustand';
import { useAuthStore } from './authStore';
import { decodeJWT } from '../utils/jwt';

export interface FamilyMember {
  userId: string;
  email: string;
  name: string;
  role: 'owner' | 'parent' | 'child' | 'adult';
  color: string;
  avatar?: string;
  joinedAt: string;
}

export interface Family {
  familyId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  ownerId: string;
  members: FamilyMember[];
  avatar?: string;
  location?: {
    city?: string;
    latitude?: number;
    longitude?: number;
  };
}

export interface Invitation {
  email: string;
  familyId: string;
  invitationId: string;
  familyName: string;
  invitedBy: string;
  invitedByName: string;
  role: 'parent' | 'adult';
  name: string;
  color: string;
  status: 'pending' | 'accepted' | 'cancelled';
  createdAt: string;
  expiresAt: number;
  acceptedAt?: string;
}

interface FamilyState {
  family: Family | null;
  invitations: Invitation[];
  isLoading: boolean;
  error: string | null;
  fetchFamily: () => Promise<void>;
  createFamily: (name: string) => Promise<void>;
  updateFamily: (updates: { name?: string; avatar?: string; location?: { city?: string; latitude?: number; longitude?: number } }) => Promise<void>;
  addMember: (email: string | undefined, name: string, role: 'parent' | 'child' | 'adult', color: string, avatar?: string) => Promise<void>;
  updateMember: (memberId: string, updates: { name?: string; role?: 'owner' | 'parent' | 'child' | 'adult'; color?: string; avatar?: string }) => Promise<void>;
  removeMember: (memberId: string) => Promise<void>;
  createInvitation: (email: string, name: string, role: 'parent' | 'adult', color: string) => Promise<{ action: 'invited' | 'added'; invitation?: Invitation; member?: FamilyMember }>;
  listInvitations: (status?: 'pending' | 'accepted' | 'cancelled') => Promise<void>;
  resendInvitation: (invitationId: string) => Promise<void>;
  cancelInvitation: (invitationId: string) => Promise<void>;
  clearError: () => void;
}

// In development, always use proxy to avoid CORS issues when accessing from network devices
// In production, use configured endpoint or default to production API
import { getApiUrl, getApiKey } from '../utils/apiConfig';

// Get API configuration (throws if not configured)
const API_URL = getApiUrl();
const API_KEY = getApiKey();

// Helper to get headers with auth token and API key
const getHeaders = (): HeadersInit => {
  const { idToken, isFamilyMode } = useAuthStore.getState();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (API_KEY) {
    headers['X-Api-Key'] = API_KEY;
  }
  if (idToken) {
    // API Gateway Cognito authorizer requires the ID token, not the access token
    headers['Authorization'] = `Bearer ${idToken}`;
  }
  if (isFamilyMode) {
    headers['X-Family-Mode'] = 'true';
    headers['X-Attribution-Actor'] = 'family';
  }
  return headers;
};

export const useFamilyStore = create<FamilyState>((set, get) => ({
  family: null,
  invitations: [],
  isLoading: false,
  error: null,

  fetchFamily: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await fetch(`${API_URL}/families`, {
        method: 'GET',
        headers: getHeaders(),
      });

      if (!response.ok) {
        // If unauthorized (401), try to refresh token first
        if (response.status === 401) {
          const authStore = useAuthStore.getState();
          try {
            // Try to refresh the token
            await authStore.refreshAccessToken();
            // Retry the request with new token
            const retryResponse = await fetch(`${API_URL}/families`, {
              method: 'GET',
              headers: getHeaders(),
            });
            
            if (!retryResponse.ok) {
              // If refresh didn't help, sign out
              authStore.signOut();
              throw new Error('Authentication failed. Please sign in again.');
            }
            
            // Success after refresh - continue with normal flow
            const data = await retryResponse.json();
            set({
              family: data.family,
              isLoading: false,
              error: null,
            });
            return;
          } catch (refreshError) {
            // Refresh failed, sign out
            authStore.signOut();
            throw new Error('Authentication failed. Please sign in again.');
          }
        }
        // For 403 (forbidden), don't sign out - it's a permissions issue, not auth
        const error = await response.json();
        throw new Error(error.message || 'Failed to fetch family');
      }

      const data = await response.json();
      set({
        family: data.family,
        isLoading: false,
        error: null,
      });
    } catch (err: any) {
      set({
        isLoading: false,
        error: err.message || 'Failed to fetch family',
        family: null,
      });
      // Re-throw authentication errors so components can handle them
      if (err.message && err.message.includes('Authentication failed')) {
        throw err;
      }
    }
  },

  createFamily: async (name: string) => {
    set({ error: null });
    
    // Store original state for rollback
    const originalFamily = get().family;
    
    // Get current user ID from token
    const { idToken } = useAuthStore.getState();
    let currentUserId: string | null = null;
    if (idToken) {
      const decoded = decodeJWT(idToken);
      currentUserId = decoded?.sub || null;
    }
    
    if (!currentUserId) {
      throw new Error('Unable to get user ID. Please sign in again.');
    }
    
    // Create optimistic family object
    const now = new Date().toISOString();
    const optimisticFamily: Family = {
      familyId: `temp-${Date.now()}`,
      name,
      createdAt: now,
      updatedAt: now,
      ownerId: currentUserId,
      members: [],
    };
    
    // Optimistic update
    set({ family: optimisticFamily });
    
    try {
      const response = await fetch(`${API_URL}/families`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ name }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create family');
      }

      const data = await response.json();
      // Update with server response (authoritative data)
      set({
        family: data.family,
        isLoading: false,
        error: null,
      });
    } catch (err: any) {
      console.error('Error creating family:', err);
      // Rollback on error
      set({ 
        family: originalFamily,
        isLoading: false,
        error: err.message || 'Failed to create family',
      });
      throw err;
    }
  },

  updateFamily: async (updates: { name?: string; avatar?: string; location?: { city?: string; latitude?: number; longitude?: number } }) => {
    set({ error: null });
    
    // Store original state for rollback
    const originalFamily = get().family;
    if (!originalFamily) {
      throw new Error('No family loaded');
    }
    
    // Optimistic update
    set((state) => ({
      family: state.family ? {
        ...state.family,
        ...updates,
        updatedAt: new Date().toISOString(),
      } : null,
    }));
    
    try {
      const response = await fetch(`${API_URL}/families`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update family');
      }

      const data = await response.json();
      // Update with server response (authoritative data)
      set({
        family: data.family,
        isLoading: false,
        error: null,
      });
    } catch (err: any) {
      console.error('Error updating family:', err);
      // Rollback on error
      set({
        family: originalFamily,
        isLoading: false,
        error: err.message || 'Failed to update family',
      });
      throw err;
    }
  },

  addMember: async (email: string | undefined, name: string, role: 'parent' | 'child' | 'adult', color: string, avatar?: string) => {
    set({ error: null });
    
    // Store original state for rollback
    const originalFamily = get().family;
    if (!originalFamily) {
      throw new Error('No family loaded');
    }
    
    // Create optimistic member
    const now = new Date().toISOString();
    const optimisticMember: FamilyMember = {
      userId: `temp-${Date.now()}`,
      email: email || '',
      name,
      role,
      color,
      avatar,
      joinedAt: now,
    };
    
    // Optimistic update
    set((state) => ({
      family: state.family ? {
        ...state.family,
        members: [...state.family.members, optimisticMember],
        updatedAt: now,
      } : null,
    }));
    
    try {
      const response = await fetch(`${API_URL}/families/members`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ email, name, role, color, avatar }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to add member');
      }

      const data = await response.json();
      // Update with server response (authoritative data with real member IDs)
      set({
        family: data.family,
        isLoading: false,
        error: null,
      });
    } catch (err: any) {
      console.error('Error adding member:', err);
      // Rollback on error
      set({
        family: originalFamily,
        isLoading: false,
        error: err.message || 'Failed to add member',
      });
      throw err;
    }
  },

  updateMember: async (memberId: string, updates: { name?: string; role?: 'owner' | 'parent' | 'child' | 'adult'; color?: string; avatar?: string }) => {
    set({ error: null });
    
    // Store original state for rollback
    const originalFamily = get().family;
    if (!originalFamily) {
      throw new Error('No family loaded');
    }
    
    const originalMember = originalFamily.members.find(m => m.userId === memberId);
    if (!originalMember) {
      throw new Error('Member not found');
    }
    
    // Optimistic update
    set((state) => ({
      family: state.family ? {
        ...state.family,
        members: state.family.members.map(m =>
          m.userId === memberId ? { ...m, ...updates } : m
        ),
        updatedAt: new Date().toISOString(),
      } : null,
    }));
    
    try {
      const response = await fetch(`${API_URL}/families/members/${memberId}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update member');
      }

      const data = await response.json();
      // Update with server response (authoritative data)
      set({
        family: data.family,
        isLoading: false,
        error: null,
      });
    } catch (err: any) {
      console.error('Error updating member:', err);
      // Rollback on error
      set({
        family: originalFamily,
        isLoading: false,
        error: err.message || 'Failed to update member',
      });
      throw err;
    }
  },

  removeMember: async (memberId: string) => {
    set({ error: null });
    
    // Store original state for rollback
    const originalFamily = get().family;
    if (!originalFamily) {
      throw new Error('No family loaded');
    }
    
    // Optimistic update - remove member immediately
    set((state) => ({
      family: state.family ? {
        ...state.family,
        members: state.family.members.filter(m => m.userId !== memberId),
        updatedAt: new Date().toISOString(),
      } : null,
    }));
    
    try {
      const response = await fetch(`${API_URL}/families/members/${memberId}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to remove member');
      }

      const data = await response.json();
      // Update with server response (confirms removal)
      set({
        family: data.family,
        isLoading: false,
        error: null,
      });
    } catch (err: any) {
      console.error('Error removing member:', err);
      // Rollback on error
      set({
        family: originalFamily,
        isLoading: false,
        error: err.message || 'Failed to remove member',
      });
      throw err;
    }
  },

  createInvitation: async (email: string, name: string, role: 'parent' | 'adult', color: string) => {
    set({ error: null });
    
    // Store original state for rollback
    const originalFamily = get().family;
    const originalInvitations = get().invitations;
    
    if (!originalFamily) {
      throw new Error('No family loaded');
    }
    
    // Get current user info for invitation
    const { idToken } = useAuthStore.getState();
    let currentUserId: string | null = null;
    let currentUserName: string | null = null;
    if (idToken) {
      const decoded = decodeJWT(idToken);
      currentUserId = decoded?.sub || null;
      currentUserName = decoded?.name || decoded?.email || null;
    }
    
    // We can't know ahead of time if it will be 'added' or 'invited'
    // So we'll optimistically add to invitations, and if it's 'added', we'll handle it in the response
    const now = new Date();
    const optimisticInvitation: Invitation = {
      email,
      familyId: originalFamily.familyId,
      invitationId: `temp-${Date.now()}`,
      familyName: originalFamily.name,
      invitedBy: currentUserId || '',
      invitedByName: currentUserName || 'Unknown',
      role,
      name,
      color,
      status: 'pending',
      createdAt: now.toISOString(),
      expiresAt: Math.floor(now.getTime() / 1000) + (30 * 24 * 60 * 60), // 30 days
    };
    
    // Optimistic update - add to invitations
    set((state) => ({
      invitations: [...state.invitations, optimisticInvitation],
    }));
    
    try {
      const response = await fetch(`${API_URL}/families/invitations`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ email, name, role, color }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create invitation');
      }

      const data = await response.json();
      
      // Handle response based on action
      if (data.action === 'added') {
        // Member was added immediately - remove from invitations, update family
        const familyResponse = await fetch(`${API_URL}/families`, {
          method: 'GET',
          headers: getHeaders(),
        });
        if (familyResponse.ok) {
          const familyData = await familyResponse.json();
          set({ 
            family: familyData.family,
            invitations: originalInvitations, // Remove optimistic invitation
            isLoading: false,
            error: null,
          });
        } else {
          // If fetch fails, still remove optimistic invitation
          set({
            invitations: originalInvitations,
            isLoading: false,
            error: null,
          });
        }
      } else {
        // Invitation was created - replace optimistic with real invitation
        set((state) => ({
          invitations: state.invitations.map(inv =>
            inv.invitationId === optimisticInvitation.invitationId
              ? data.invitation
              : inv
          ),
          isLoading: false,
          error: null,
        }));
      }

      return data;
    } catch (err: any) {
      console.error('Error creating invitation:', err);
      // Rollback on error
      set({
        family: originalFamily,
        invitations: originalInvitations,
        isLoading: false,
        error: err.message || 'Failed to create invitation',
      });
      throw err;
    }
  },

  listInvitations: async (status: 'pending' | 'accepted' | 'cancelled' = 'pending') => {
    set({ isLoading: true, error: null });
    try {
      const response = await fetch(`${API_URL}/families/invitations?status=${status}`, {
        method: 'GET',
        headers: getHeaders(),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to list invitations');
      }

      const data = await response.json();
      set({
        invitations: data.invitations || [],
        isLoading: false,
        error: null,
      });
    } catch (err: any) {
      set({
        isLoading: false,
        error: err.message || 'Failed to list invitations',
      });
      throw err;
    }
  },

  resendInvitation: async (invitationId: string) => {
    set({ error: null });
    // No optimistic update needed - just send the email
    // The invitation data doesn't change, only the email is sent
    try {
      const response = await fetch(`${API_URL}/families/invitations/${invitationId}`, {
        method: 'POST',
        headers: getHeaders(),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to resend invitation');
      }

      // Success - no state update needed, invitation data is unchanged
      set({ error: null });
    } catch (err: any) {
      set({
        error: err.message || 'Failed to resend invitation',
      });
      throw err;
    }
  },

  cancelInvitation: async (invitationId: string) => {
    set({ error: null });
    
    // Store original state for rollback
    const originalInvitations = get().invitations;
    
    // Optimistic update - remove invitation immediately
    set((state) => ({
      invitations: state.invitations.filter(inv => inv.invitationId !== invitationId),
    }));
    
    try {
      const response = await fetch(`${API_URL}/families/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to cancel invitation');
      }

      // Refresh invitations list to get authoritative state
      const store = useFamilyStore.getState();
      await store.listInvitations('pending');

      set({ isLoading: false, error: null });
    } catch (err: any) {
      console.error('Error canceling invitation:', err);
      // Rollback on error
      set({
        invitations: originalInvitations,
        isLoading: false,
        error: err.message || 'Failed to cancel invitation',
      });
      throw err;
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));

